/**
 * Cartel exportable para redes (feed cuadrado / story vertical).
 * Estilo moderno alineado con marca Cadencya.
 */
export const CADENCYA_BRAND_PURPLE = '#6B46C1';
export const CADENCYA_DEEP = '#4C1D95';
export const CADENCYA_SOFT = '#EDE9FE';

export const RRSS_CARTEL_FORMATOS = {
  feed: {
    key: 'feed',
    w: 1080,
    h: 1080,
    title: 'Feed / publicación',
    subtitle: 'Cuadrado 1080 × 1080 (Instagram, Facebook…)',
  },
  story: {
    key: 'story',
    w: 1080,
    h: 1920,
    title: 'Story / Short / Reels',
    subtitle: 'Vertical 1080 × 1920 (9:16)',
  },
};

export function defaultRrssCartelOpts() {
  return {
    titulo: true,
    fecha: true,
    horas: true,
    ubicacion: true,
    ubicacionLlegada: false,
    tipoActuacion: false,
    repertorio: false,
    observaciones: false,
    cartelFondo: false,
    // Colores (personalizables desde el modal)
    bgColor: '#FFFFFF',
    textColor: '#0F172A',
    accentColor: CADENCYA_DEEP,
    // Opacidad del cartel cuando se usa de fondo (0..1)
    cartelOpacity: 0.75,
    logoBanda: true,
    nombreBanda: true,
    footerCadencya: true,
  };
}

function wrapText(ctx, text, maxWidth) {
  const t = String(text || '').trim();
  if (!t) return [];
  const words = t.split(/\s+/);
  const lines = [];
  let line = '';
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function roundRectPath(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function drawCover(ctx, img, dx, dy, dw, dh) {
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  if (!iw || !ih) return;
  const ir = iw / ih;
  const r = dw / dh;
  let sw;
  let sh;
  let sx;
  let sy;
  if (ir > r) {
    sh = ih;
    sw = sh * r;
    sx = (iw - sw) / 2;
    sy = 0;
  } else {
    sw = iw;
    sh = sw / r;
    sx = 0;
    sy = (ih - sh) / 2;
  }
  ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
}

function drawContain(ctx, img, dx, dy, dw, dh) {
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  if (!iw || !ih) return;
  const scale = Math.min(dw / iw, dh / ih);
  const w = iw * scale;
  const h = ih * scale;
  const x = dx + (dw - w) / 2;
  const y = dy + (dh - h) / 2;
  ctx.drawImage(img, x, y, w, h);
}

/** Fondo suave con gradiente y textura de puntos muy sutil */
function drawModernBackground(ctx, width, height) {
  const g = ctx.createLinearGradient(0, 0, width, height * 1.05);
  g.addColorStop(0, '#F8F5FF');
  g.addColorStop(0.35, '#FFFFFF');
  g.addColorStop(0.7, '#F4F0FF');
  g.addColorStop(1, '#EDE9FE');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, width, height);

  // Brillo diagonal (glassmorphism sutil)
  const g2 = ctx.createLinearGradient(0, 0, width * 0.6, height * 0.4);
  g2.addColorStop(0, 'rgba(255,255,255,0.55)');
  g2.addColorStop(0.5, 'rgba(255,255,255,0)');
  g2.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g2;
  ctx.fillRect(0, 0, width, height * 0.55);

  ctx.fillStyle = 'rgba(107, 70, 193, 0.045)';
  const step = Math.max(28, Math.round(Math.min(width, height) * 0.032));
  for (let x = 0; x < width + step; x += step) {
    for (let y = 0; y < height + step; y += step) {
      if ((((x / step) | 0) + ((y / step) | 0)) % 2 === 0) {
        ctx.beginPath();
        ctx.arc(x, y, 1.2, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  // Barra superior decorativa
  const topBar = ctx.createLinearGradient(0, 0, width, 0);
  topBar.addColorStop(0, CADENCYA_DEEP);
  topBar.addColorStop(0.35, CADENCYA_BRAND_PURPLE);
  topBar.addColorStop(0.65, '#7C3AED');
  topBar.addColorStop(1, CADENCYA_DEEP);
  ctx.fillStyle = topBar;
  ctx.fillRect(0, 0, width, Math.max(8, Math.round(height * 0.006)));
}

function drawCartelHeader(ctx, cartelImg, width, headerH) {
  ctx.save();
  const rBottom = Math.min(36, Math.round(headerH * 0.08));
  roundRectPath(ctx, 0, 0, width, headerH + rBottom, rBottom);
  ctx.clip();
  drawCover(ctx, cartelImg, 0, 0, width, headerH + rBottom);
  ctx.restore();

  // Viñeta inferior para transición elegante
  const vg = ctx.createLinearGradient(0, headerH * 0.35, 0, headerH + rBottom);
  vg.addColorStop(0, 'rgba(76, 29, 149, 0)');
  vg.addColorStop(0.55, 'rgba(76, 29, 149, 0.25)');
  vg.addColorStop(1, 'rgba(76, 29, 149, 0.72)');
  ctx.fillStyle = vg;
  ctx.fillRect(0, 0, width, headerH + rBottom);

  // Sombra bajo la cabecera
  ctx.fillStyle = 'rgba(76, 29, 149, 0.12)';
  ctx.fillRect(0, headerH + rBottom - 2, width, 4);
}

function drawPosterBackgroundFromCartel(ctx, cartelImg, width, height, { bgColor = '#FFFFFF', cartelOpacity = 0.75 } = {}) {
  ctx.save();
  ctx.fillStyle = bgColor;
  ctx.fillRect(0, 0, width, height);

  // Cartel directo, en contain para evitar zoom excesivo.
  ctx.globalAlpha = Math.max(0, Math.min(1, Number(cartelOpacity) || 0.75));
  drawContain(ctx, cartelImg, 0, 0, width, height);
  ctx.globalAlpha = 1;
  ctx.restore();
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {object} params
 * @param {number} params.width
 * @param {number} params.height
 * @param {object} params.opts mismo shape que defaultRrssCartelOpts()
 * @param {object} params.content textos ya formateados para el cartel
 * @param {HTMLImageElement | null} params.cadencyaLogo
 * @param {HTMLImageElement | null} params.bandaAvatar
 * @param {HTMLImageElement | null} params.cartelImg
 */
export function drawActuacionCartelRrss(canvas, { width, height, opts, content, cadencyaLogo, bandaAvatar, cartelImg }) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  canvas.width = width;
  canvas.height = height;

  const isStory = height > width;
  const pad = Math.round(Math.min(width, height) * (isStory ? 0.048 : 0.05));
  const footerH = opts.footerCadencya ? Math.max(96, Math.round(height * (isStory ? 0.078 : 0.092))) : 0;
  let yTop = pad;

  const bgColor = opts?.bgColor || '#FFFFFF';
  const textColor = opts?.textColor || '#0F172A';
  const accentColor = opts?.accentColor || CADENCYA_DEEP;

  if (opts.cartelFondo && cartelImg) {
    drawPosterBackgroundFromCartel(ctx, cartelImg, width, height, { bgColor, cartelOpacity: opts?.cartelOpacity });
    // Barra superior decorativa (mantiene identidad incluso con fondo)
    ctx.fillStyle = accentColor;
    ctx.fillRect(0, 0, width, Math.max(8, Math.round(height * 0.006)));
  } else {
    drawModernBackground(ctx, width, height);
  }

  const contentBottom = height - footerH - pad * 1.1;
  let y = yTop;
  const maxTextW = width - pad * 2;
  const cardPadX = Math.round(pad * 0.85);
  const cardRadius = Math.min(28, Math.round(width * 0.026));

  const bandName = (content.bandaNombre && String(content.bandaNombre).trim()) || '';
  const showBandRow = (opts.logoBanda && bandaAvatar) || (opts.nombreBanda && bandName);

  if (showBandRow) {
    const rowH = Math.round(Math.min(width, height) * (isStory ? 0.085 : 0.095));
    const rowY = y;
    let xCursor = pad;
    const yMid = rowY + rowH / 2;

    if (opts.logoBanda) {
      const avSize = rowH;
      const cx = xCursor + avSize / 2;
      const cy = yMid;
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, avSize / 2 + 3, 0, Math.PI * 2);
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 4;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, cy, avSize / 2 + 1, 0, Math.PI * 2);
      ctx.strokeStyle = accentColor;
      ctx.lineWidth = 2.5;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, cy, avSize / 2, 0, Math.PI * 2);
      ctx.clip();
      if (bandaAvatar) {
        ctx.drawImage(bandaAvatar, xCursor, cy - avSize / 2, avSize, avSize);
      } else {
        // Placeholder visible para detectar si el layout está bien aunque no cargue el avatar.
        ctx.fillStyle = 'rgba(107, 70, 193, 0.12)';
        ctx.fillRect(xCursor, cy - avSize / 2, avSize, avSize);
        ctx.fillStyle = CADENCYA_DEEP;
        const fs0 = Math.max(10, Math.round(avSize * 0.22));
        ctx.font = `800 ${fs0}px ui-sans-serif, system-ui, "Segoe UI", sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('LOGO', xCursor + avSize / 2, cy);
      }
      ctx.restore();
      xCursor += avSize + Math.round(pad * 0.65);
    }

    if (opts.nombreBanda && bandName) {
      ctx.fillStyle = textColor;
      const fs = Math.round(rowH * 0.36);
      ctx.font = `700 ${fs}px ui-sans-serif, system-ui, "Segoe UI", sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = 'rgba(255,255,255,0.55)';
      ctx.shadowBlur = 12;
      ctx.shadowOffsetY = 1;
      const lines = wrapText(ctx, bandName, width - xCursor - pad);
      lines.slice(0, 2).forEach((ln, i) => {
        ctx.fillText(ln, xCursor, yMid + (i - (lines.length - 1) / 2) * (fs * 1.15));
      });
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;
    }
    y += rowH + Math.round(pad * 0.55);
  }

  const titleSize = Math.round(Math.min(width, height) * (isStory ? 0.054 : 0.058));
  const bodySize = Math.round(titleSize * 0.5);
  const labelSize = Math.round(titleSize * 0.34);

  if (opts.titulo && content.titulo) {
    const barW = Math.max(6, Math.round(width * 0.012));
    const titleStr = String(content.titulo);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.font = `800 ${titleSize}px ui-sans-serif, system-ui, "Segoe UI", sans-serif`;
    const titleLines = wrapText(ctx, titleStr, maxTextW - barW - 16);
    const maxTitleLines = isStory ? 4 : 3;
    const tl = titleLines.slice(0, maxTitleLines);
    const firstBlockH = tl.length * titleSize * 1.12 + 8;

    const gradBar = ctx.createLinearGradient(pad, y, pad, y + firstBlockH + 8);
    gradBar.addColorStop(0, accentColor);
    gradBar.addColorStop(1, CADENCYA_BRAND_PURPLE);
    ctx.fillStyle = gradBar;
    roundRectPath(ctx, pad, y, barW, Math.min(firstBlockH + 8, titleSize * 3.8), barW / 2);
    ctx.fill();

    ctx.fillStyle = textColor;
    ctx.font = `800 ${titleSize}px ui-sans-serif, system-ui, "Segoe UI", sans-serif`;
    let ty = y;
    tl.forEach((ln) => {
      if (ty > contentBottom - bodySize * 3) return;
      ctx.shadowColor = 'rgba(15, 23, 42, 0.06)';
      ctx.shadowBlur = 12;
      ctx.shadowOffsetY = 2;
      ctx.fillText(ln, pad + barW + 16, ty);
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;
      ty += titleSize * 1.1;
    });
    y = ty + pad * 0.45;
  }

  const drawMetaCard = (label, value) => {
    if (!value || y > contentBottom - labelSize * 3) return;
    const innerPad = Math.round(pad * 0.75);
    const labelUpper = String(label).toUpperCase();
    ctx.font = `500 ${bodySize}px ui-sans-serif, system-ui, "Segoe UI", sans-serif`;
    const valueLines = wrapText(ctx, String(value), maxTextW - innerPad * 2 - 14);
    let valueH = 0;
    valueLines.forEach(() => {
      valueH += bodySize * 1.22;
    });
    const cardH = innerPad * 2 + labelSize * 1.35 + valueH + 6;
    const cardW = width - pad * 2 + 8;
    const cardX = pad - 4;

    ctx.save();
    ctx.shadowColor = 'rgba(15, 23, 42, 0.06)';
    ctx.shadowBlur = 20;
    ctx.shadowOffsetY = 6;
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    roundRectPath(ctx, cardX, y, cardW, cardH, cardRadius);
    ctx.fill();
    ctx.restore();

    ctx.strokeStyle = 'rgba(107, 70, 193, 0.12)';
    ctx.lineWidth = 1;
    roundRectPath(ctx, cardX, y, cardW, cardH, cardRadius);
    ctx.stroke();

    ctx.fillStyle = CADENCYA_BRAND_PURPLE;
    roundRectPath(ctx, cardX + innerPad, y + innerPad + 2, 4, labelSize * 1.1, 2);
    ctx.fill();

    ctx.fillStyle = '#6B46C1';
    ctx.font = `700 ${labelSize}px ui-sans-serif, system-ui, "Segoe UI", sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.letterSpacing = `${Math.max(0.5, labelSize * 0.04)}px`;
    ctx.fillText(labelUpper, cardX + innerPad + 14, y + innerPad);
    ctx.letterSpacing = '0px';

    let vy = y + innerPad + labelSize * 1.35;
    ctx.fillStyle = '#334155';
    ctx.font = `500 ${bodySize}px ui-sans-serif, system-ui, "Segoe UI", sans-serif`;
    valueLines.forEach((ln) => {
      if (vy > contentBottom - bodySize) return;
      ctx.fillText(ln, cardX + innerPad, vy);
      vy += bodySize * 1.22;
    });

    y += cardH + Math.round(pad * 0.38);
  };

  // --- INFO compacta (una sola tarjeta) para no comerse el espacio del repertorio ---
  const metaItems = [];
  if (opts.fecha && content.fecha) metaItems.push({ label: 'Fecha', value: content.fecha });
  if (opts.horas && content.horario) metaItems.push({ label: 'Horario', value: content.horario });
  if (opts.tipoActuacion && content.tipoLabel) metaItems.push({ label: 'Tipo', value: content.tipoLabel });
  if (opts.ubicacion && content.ubicacion) metaItems.push({ label: 'Ubicación', value: content.ubicacion });
  if (opts.ubicacionLlegada && content.ubicacionFin) metaItems.push({ label: 'Llegada', value: content.ubicacionFin });
  if (opts.observaciones && content.observaciones) metaItems.push({ label: 'Notas', value: content.observaciones });

  const drawMetaGridCard = () => {
    if (!metaItems.length || y > contentBottom - labelSize * 3) return;

    const innerPad = Math.round(pad * 0.75);
    const cardW = width - pad * 2;
    const cardX = pad;

    // Si hay repertorio, comprimimos la info para dejar espacio (especialmente en feed cuadrado).
    const compact = !!opts.repertorio && !isStory;
    const cols = compact ? 3 : (isStory ? 1 : 2);
    const colGapX = Math.round(innerPad * 0.9);
    const colW = (cardW - innerPad * 2 - (cols - 1) * colGapX) / cols;

    const labelFs = Math.round(labelSize * (compact ? 0.82 : 0.92));
    const valueFs = Math.round(bodySize * (compact ? 0.82 : 0.95));
    const rowGap = Math.max(6, Math.round(pad * (compact ? 0.14 : 0.22)));
    const cellGapY = Math.max(4, Math.round(pad * (compact ? 0.08 : 0.12)));
    const maxValueLines = compact ? 1 : 2;

    // Las "Notas" (observaciones) no deben ir en una celda estrecha: se dibujan a ancho completo.
    const notasItem = metaItems.find((it) => String(it.label || '').toLowerCase() === 'notas') || null;
    const gridItems = metaItems.filter((it) => it !== notasItem);

    const measureCells = (items) => {
      const cells = items.map((it) => {
        const label = String(it.label || '').toUpperCase();
        ctx.font = `500 ${valueFs}px ui-sans-serif, system-ui, "Segoe UI", sans-serif`;
        const vLines = wrapText(ctx, String(it.value || ''), colW).slice(0, maxValueLines);
        const h = labelFs * 1.25 + vLines.length * valueFs * 1.18 + cellGapY;
        return { label, vLines, h };
      });
      const rows = Math.ceil(cells.length / cols);
      const rowHeights = [];
      for (let r = 0; r < rows; r += 1) {
        let mh = 0;
        for (let c = 0; c < cols; c += 1) {
          const idx = r * cols + c;
          if (!cells[idx]) continue;
          mh = Math.max(mh, cells[idx].h);
        }
        rowHeights.push(mh);
      }
      const contentH = rowHeights.reduce((a, b) => a + b, 0) + (rows - 1) * rowGap;
      const cardH = innerPad * 2 + contentH;
      return { cells, rows, rowHeights, cardH, contentH, rowsCount: rows };
    };

    // Si no cabe, soltamos primero lo menos prioritario del grid (llegada, tipo...) pero mantenemos "Notas" aparte.
    const prioritized = [...gridItems];
    let measured = measureCells(prioritized);

    const measureNotas = (maxLines) => {
      if (!notasItem?.value) return { lines: [], h: 0, maxLines: 0 };
      ctx.font = `500 ${valueFs}px ui-sans-serif, system-ui, "Segoe UI", sans-serif`;
      const w = cardW - innerPad * 2;
      const raw = String(notasItem.value || '').trim();
      const lines = raw
        ? wrapText(ctx, raw.replace(/\n+/g, ' · '), w).slice(0, maxLines)
        : [];
      const hasMore = raw && wrapText(ctx, raw.replace(/\n+/g, ' · '), w).length > maxLines;
      const safeLines = hasMore && lines.length
        ? [...lines.slice(0, Math.max(1, lines.length - 1)), `${lines[lines.length - 1].replace(/\s+$/, '')}…`]
        : lines;
      const h = safeLines.length
        ? (labelFs * 1.25 + cellGapY + safeLines.length * valueFs * 1.18 + Math.round(pad * 0.12))
        : 0;
      return { lines: safeLines, h, maxLines };
    };

    let notasMeasured = measureNotas(compact ? 3 : 5);
    const extraNotasGap = notasMeasured.h ? Math.max(6, Math.round(pad * 0.18)) : 0;
    let cardH = measured.cardH + (notasMeasured.h ? (extraNotasGap + notasMeasured.h) : 0);

    while (compact && (y + cardH > contentBottom - labelSize * 2) && prioritized.length > 3) {
      prioritized.pop();
      measured = measureCells(prioritized);
      cardH = measured.cardH + (notasMeasured.h ? (extraNotasGap + notasMeasured.h) : 0);
    }
    // Si aún no cabe, recortamos líneas de notas antes de renunciar a dibujar la tarjeta entera.
    while (compact && notasMeasured.h && (y + cardH > contentBottom - labelSize * 2) && notasMeasured.maxLines > 1) {
      notasMeasured = measureNotas(notasMeasured.maxLines - 1);
      cardH = measured.cardH + (notasMeasured.h ? (extraNotasGap + notasMeasured.h) : 0);
    }
    if (y + cardH > contentBottom - labelSize * 2) return;

    let cy = y + innerPad;
    for (let r = 0; r < measured.rows; r += 1) {
      let cx = cardX + innerPad;
      for (let c = 0; c < cols; c += 1) {
        const idx = r * cols + c;
        const cell = measured.cells[idx];
        if (cell) {
          ctx.fillStyle = '#4C1D95';
          ctx.font = `800 ${labelFs}px ui-sans-serif, system-ui, "Segoe UI", sans-serif`;
          ctx.textAlign = 'left';
          ctx.textBaseline = 'top';
          ctx.shadowColor = 'rgba(255,255,255,0.55)';
          ctx.shadowBlur = 10;
          ctx.shadowOffsetY = 1;
          ctx.fillText(cell.label, cx, cy);
          ctx.shadowBlur = 0;
          ctx.shadowOffsetY = 0;
          let vy = cy + labelFs * 1.15 + cellGapY;
          ctx.fillStyle = '#0F172A';
          ctx.font = `500 ${valueFs}px ui-sans-serif, system-ui, "Segoe UI", sans-serif`;
          cell.vLines.forEach((ln) => {
            ctx.fillText(ln, cx, vy);
            vy += valueFs * 1.18;
          });
        }
        cx += colW + colGapX;
      }
      cy += measured.rowHeights[r] + rowGap;
    }

    // Notas a ancho completo (debajo del grid)
    if (notasMeasured.h && notasMeasured.lines.length) {
      cy += extraNotasGap;
      ctx.fillStyle = '#4C1D95';
      ctx.font = `800 ${labelFs}px ui-sans-serif, system-ui, "Segoe UI", sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.fillText('NOTAS', cardX + innerPad, cy);
      let vy = cy + labelFs * 1.15 + cellGapY;
      ctx.fillStyle = '#0F172A';
      ctx.font = `500 ${valueFs}px ui-sans-serif, system-ui, "Segoe UI", sans-serif`;
      notasMeasured.lines.forEach((ln) => {
        if (vy > contentBottom - valueFs) return;
        ctx.fillText(ln, cardX + innerPad, vy);
        vy += valueFs * 1.18;
      });
      cy += notasMeasured.h;
    }

    y += cardH + Math.round(pad * 0.18);
  };

  drawMetaGridCard();

  // --- Repertorio: ocupar el espacio restante, 1 o 2 columnas según cantidad ---
  if (opts.repertorio && content.repertorioLines?.length && y <= contentBottom - labelSize * 2) {
    // Un poco más compacto para ganar espacio vertical en feed.
    const innerPad = Math.round(pad * (isStory ? 0.72 : 0.62));
    const cardW = width - pad * 2;
    const cardX = pad;
    const titleFs = Math.round(labelSize * 1.02);
    // Repertorio ligeramente más pequeño para evitar solapes y meter más obras.
    const repFs = Math.max(13, Math.round(bodySize * (isStory ? 0.88 : 0.84)));
    const lineH = Math.round(repFs * 1.14);
    // Reducimos cabecera para subir el listado (había demasiado margen).
    const headerH = Math.round(innerPad + titleFs * 1.28);
    const availH = Math.max(0, contentBottom - y - 8);
    const repH = Math.max(Math.min(availH, Math.round(height * (isStory ? 0.42 : 0.46))), Math.round(height * 0.16));
    if (repH > innerPad * 3 + lineH * 4) {
      // Header
      ctx.fillStyle = '#4C1D95';
      ctx.font = `900 ${titleFs}px ui-sans-serif, system-ui, "Segoe UI", sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.shadowColor = 'rgba(255,255,255,0.55)';
      ctx.shadowBlur = 12;
      ctx.shadowOffsetY = 1;
      ctx.fillText(`REPERTORIO (${content.repertorioLines.length})`, cardX + innerPad, y + Math.round(innerPad * 0.92));
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;

      const colGap = Math.round(innerPad * 0.9);
      const listTop = y + headerH;
      const listH = y + repH - innerPad - listTop;

      // En feed y story, priorizamos 2 columnas antes para reducir truncado (+N más…)
      // y aprovechar mejor el espacio, sobre todo cuando hay muchas obras.
      const preferTwoCols = content.repertorioLines.length >= (isStory ? 14 : 12);

      const computeLayout = (cols) => {
        const colW = (cardW - innerPad * 2 - (cols - 1) * colGap);
        const colInnerW = colW / cols;
        const maxLinesPerCol = Math.max(1, Math.floor(listH / lineH));
        const maxTotalLines = maxLinesPerCol * cols;
        return { cols, colW, colInnerW, maxLinesPerCol, maxTotalLines };
      };

      const computeWrappedLines = (layout) => {
        /** @type {Array<{ workIdx: number, line: string }>} */
        const out = [];
        // IMPORTANTE: medir con la misma fuente con la que se dibuja el repertorio,
        // si no, el wrap puede quedar mal y se solapan columnas.
        ctx.font = `500 ${repFs}px ui-sans-serif, system-ui, "Segoe UI", sans-serif`;
        for (let workIdx = 0; workIdx < content.repertorioLines.length; workIdx += 1) {
          const raw = content.repertorioLines[workIdx];
          // Solo mostramos el título (sin autor). Esperamos formatos típicos tipo "Título — Autor"
          // o "Título - Autor" (si existe), y dejamos intacto lo demás.
          const s = String(raw || '').trim();
          const titleOnly = s.split(/\s+[—-]\s+/)[0] || s;
          const w = wrapText(ctx, titleOnly.trim(), layout.colInnerW);
          for (const ln of w) out.push({ workIdx, line: ln });
          if (out.length > layout.maxTotalLines + 40) break;
        }
        return out;
      };

      const countShownWorks = (wrapped, maxLines) => {
        const seen = new Set();
        for (let i = 0; i < Math.min(maxLines, wrapped.length); i += 1) {
          seen.add(wrapped[i].workIdx);
        }
        return seen.size;
      };

      let layout = computeLayout(preferTwoCols ? 2 : 1);
      let wrapped = computeWrappedLines(layout);
      let shownWorks = countShownWorks(wrapped, layout.maxTotalLines);
      let remaining = Math.max(0, content.repertorioLines.length - shownWorks);

      // Si estamos truncando en 1 columna, pasamos a 2 columnas automáticamente.
      if (!preferTwoCols && remaining > 0) {
        layout = computeLayout(2);
        wrapped = computeWrappedLines(layout);
        shownWorks = countShownWorks(wrapped, layout.maxTotalLines);
        remaining = Math.max(0, content.repertorioLines.length - shownWorks);
      }

      ctx.save();
      ctx.fillStyle = '#0F172A';
      ctx.font = `500 ${repFs}px ui-sans-serif, system-ui, "Segoe UI", sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      // Clip por columna (evita solapes entre izquierda/derecha incluso si una línea queda larga)
      const maxToDraw = Math.min(layout.maxTotalLines, wrapped.length);
      for (let c = 0; c < layout.cols; c += 1) {
        const colX = cardX + innerPad + c * (layout.colInnerW + colGap);
        ctx.save();
        ctx.beginPath();
        ctx.rect(colX - 1, listTop - 2, layout.colInnerW + 2, listH + 4);
        ctx.clip();
        for (let r = 0; r < layout.maxLinesPerCol; r += 1) {
          const i = c * layout.maxLinesPerCol + r;
          if (i >= maxToDraw) break;
          const yy = listTop + r * lineH;
          ctx.fillText(wrapped[i].line, colX, yy);
        }
        ctx.restore();
      }

      ctx.restore();

      // Indicador de truncado
      if (remaining > 0) {
        ctx.fillStyle = 'rgba(15, 23, 42, 0.78)';
        ctx.font = `600 ${Math.max(12, Math.round(repFs * 0.86))}px ui-sans-serif, system-ui, "Segoe UI", sans-serif`;
        ctx.textAlign = 'right';
        ctx.textBaseline = 'alphabetic';
        ctx.fillText(`+${remaining} más…`, cardX + cardW - innerPad, y + repH - Math.max(10, Math.round(innerPad * 0.5)));
      }

      y += repH + Math.round(pad * 0.32);
    }
  }

  if (opts.footerCadencya && footerH > 0) {
    const fy = height - footerH;

    const fg = ctx.createLinearGradient(0, fy, 0, height);
    fg.addColorStop(0, '#5B21B6');
    fg.addColorStop(0.45, CADENCYA_DEEP);
    fg.addColorStop(1, '#2E1065');
    ctx.fillStyle = fg;
    ctx.fillRect(0, fy, width, footerH);

    const shine = ctx.createLinearGradient(0, fy, width, fy);
    shine.addColorStop(0, 'rgba(255,255,255,0)');
    shine.addColorStop(0.45, 'rgba(255,255,255,0.14)');
    shine.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = shine;
    ctx.fillRect(0, fy, width, 3);

    const taglineSize = Math.max(18, Math.round(footerH * 0.15));
    const taglineY = height - Math.max(16, footerH * 0.14);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = 'rgba(255,255,255,0.72)';
    ctx.font = `600 ${taglineSize}px ui-sans-serif, system-ui, "Segoe UI", sans-serif`;
    ctx.fillText('cadencya.com', width / 2, taglineY);

    const taglineBlock = taglineSize + Math.max(16, footerH * 0.12);
    const logoAreaTop = fy + Math.max(14, footerH * 0.1);
    const logoAreaBottom = taglineY - taglineBlock * 0.35;
    const logoAreaH = Math.max(40, logoAreaBottom - logoAreaTop);

    let drewLogo = false;
    if (cadencyaLogo) {
      const lw = cadencyaLogo.naturalWidth || cadencyaLogo.width;
      const lh = cadencyaLogo.naturalHeight || cadencyaLogo.height;
      if (lw && lh) {
        const maxW = width * 0.72;
        const maxH = logoAreaH;
        const scale = Math.min(maxW / lw, maxH / lh, 1);
        const dw = lw * scale;
        const dh = lh * scale;
        const lx = (width - dw) / 2;
        const ly = logoAreaTop + (logoAreaH - dh) / 2;
        ctx.save();
        ctx.globalAlpha = 1;
        ctx.drawImage(cadencyaLogo, lx, ly, dw, dh);
        ctx.restore();
        drewLogo = true;
      }
    }

    if (!drewLogo) {
      ctx.fillStyle = '#FFFFFF';
      ctx.font = `800 ${Math.round(footerH * 0.26)}px ui-sans-serif, system-ui, "Segoe UI", sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Cadencya', width / 2, logoAreaTop + logoAreaH / 2);
    }
  }
}

export function slugifyFilename(name) {
  return String(name || 'actuacion')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60) || 'actuacion';
}
